import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Chip,
  Stack,
  Card,
  CardActionArea,
  Avatar,
  Skeleton,
  InputBase,
  Paper,
  Select,
  MenuItem,
  FormControl,
  Pagination,
  Divider,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";
import {
  Search,
  LocationOnOutlined,
  StarRounded,
  RestaurantMenuOutlined,
  TuneOutlined,
  ClearOutlined,
  ArrowForward,
  Bolt,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import CompanyServices from "../../services/CompanyService";
import ImageLoader from "../../components/ImageLoader/ImageLoader";
import { PublicHeader } from "components";

const MotionCard = motion(Card);
const PAGE_SIZE = 12;

const BRAND = "#F9735B";
const BRAND_DARK = "#DC5F49";
const INK = "#0F172A";
const MUTED = "#475569";
const SOFT_TEXT = "#64748B";
const BORDER = "#E2E8F0";
const CLOUD = "#F8FAFC";

const RestaurantCardSkeleton = () => (
  <Card
    elevation={0}
    sx={{
      borderRadius: "22px",
      overflow: "hidden",
      border: `1px solid ${BORDER}`,
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      bgcolor: "#fff",
      boxShadow: "0 18px 40px rgba(15,23,42,0.05)",
    }}
  >
    <Skeleton
      variant="rectangular"
      animation="wave"
      sx={{ width: "100%", height: { xs: 190, sm: 220 }, flexShrink: 0 }}
    />
    <Box sx={{ p: { xs: 2, sm: 2.75 }, flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
      <Skeleton variant="text" width="72%" height={28} sx={{ borderRadius: "6px" }} animation="wave" />
      <Skeleton variant="text" width="48%" height={18} sx={{ borderRadius: "6px" }} animation="wave" />
      <Skeleton variant="text" width="88%" height={16} sx={{ borderRadius: "6px" }} animation="wave" />
      <Box sx={{ flex: 1 }} />
      <Skeleton variant="rectangular" height={42} sx={{ borderRadius: "12px", mt: 1 }} animation="wave" />
    </Box>
  </Card>
);

const RestaurantCard = ({ company }) => {
  const rating = parseFloat(company.averageRating) || 0;
  const totalRatings = parseInt(company.totalRatings, 10) || 0;
  const locationLabel = [company.district, company.country].filter(Boolean).join(", ");
  const summaryText = company.description || company.slogan || "Fresh menus, smooth ordering, and a better dine-in experience.";
  const currencyLabel = company.currency || "Menu Live";
  const initial = company.name?.charAt(0)?.toUpperCase() || "R";

  return (
    <MotionCard
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      elevation={0}
      sx={{
        borderRadius: "22px",
        overflow: "hidden",
        border: `1px solid ${BORDER}`,
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        position: "relative",
        boxShadow: "0 18px 40px rgba(15,23,42,0.06)",
        transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(249,115,91,0.04) 0%, rgba(255,255,255,0) 30%, rgba(14,165,233,0.04) 100%)",
          pointerEvents: "none",
        },
        "&:hover": {
          transform: { xs: "none", sm: "translateY(-6px)" },
          borderColor: alpha(BRAND, 0.45),
          boxShadow: "0 24px 52px rgba(15,23,42,0.12)",
        },
      }}
    >
      <CardActionArea
        onClick={() => window.open(`/restaurant-menu?companyId=${company.id}`, "_blank")}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          flex: 1,
          "&:hover .restaurant-card-image": { transform: "scale(1.05)" },
        }}
      >
        <Box
          sx={{
            height: { xs: 220, sm: 248 },
            position: "relative",
            overflow: "hidden",
            flexShrink: 0,
            background:
              "linear-gradient(135deg, rgba(249,115,91,0.12) 0%, rgba(14,165,233,0.12) 100%)",
          }}
        >
          {company.logo ? (
            <Box
              className="restaurant-card-image"
              sx={{
                width: "100%",
                height: "100%",
                transition: "transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <ImageLoader
                imagePath={company.logo}
                width="100%"
                height={248}
                alt={company.name}
                rounded={false}
                borderRadius={0}
              />
            </Box>
          ) : (
            <Box
              className="restaurant-card-image"
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 84, sm: 96 },
                  height: { xs: 84, sm: 96 },
                  background: "linear-gradient(135deg, #F9735B 0%, #FB923C 100%)",
                  fontSize: { xs: "2rem", sm: "2.25rem" },
                  fontWeight: 700,
                  fontFamily: '"exo", "Inter", sans-serif',
                  boxShadow: "0 18px 36px rgba(249,115,91,0.28)",
                }}
              >
                {initial}
              </Avatar>
            </Box>
          )}

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(135deg, rgba(15,23,42,0.02) 25%, transparent 25%, transparent 50%, rgba(15,23,42,0.02) 50%, rgba(15,23,42,0.02) 75%, transparent 75%, transparent)",
              backgroundSize: "30px 30px",
              opacity: 0.16,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to top, rgba(15,23,42,0.78) 0%, rgba(15,23,42,0.22) 55%, transparent 100%)",
            }}
          />

          <Box sx={{ position: "absolute", top: 14, left: 14 }}>
            <Box
              sx={{
                px: 1.4,
                py: 0.6,
                borderRadius: "999px",
                bgcolor: company.isActive ? "rgba(16,185,129,0.92)" : "rgba(15,23,42,0.72)",
                border: "1px solid rgba(255,255,255,0.2)",
                color: "#fff",
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                fontFamily: '"exo", "Inter", sans-serif',
                backdropFilter: "blur(10px)",
              }}
            >
              {company.isActive ? "Open Now" : "Closed"}
            </Box>
          </Box>

          {(rating > 0 || totalRatings > 0) && (
            <Box
              sx={{
                position: "absolute",
                top: 14,
                right: 14,
                px: 1.2,
                py: 0.55,
                borderRadius: "999px",
                bgcolor: "rgba(255,255,255,0.94)",
                border: "1px solid rgba(255,255,255,0.85)",
                display: "flex",
                alignItems: "center",
                gap: 0.45,
                boxShadow: "0 8px 18px rgba(15,23,42,0.12)",
              }}
            >
              <StarRounded sx={{ fontSize: 16, color: "#F59E0B" }} />
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  color: INK,
                  fontFamily: '"Inter", sans-serif',
                  lineHeight: 1,
                }}
              >
                {rating.toFixed(1)}
              </Typography>
              {totalRatings > 0 && (
                <Typography
                  sx={{
                    fontSize: "0.74rem",
                    fontWeight: 600,
                    color: SOFT_TEXT,
                    fontFamily: '"Inter", sans-serif',
                    lineHeight: 1,
                  }}
                >
                  ({totalRatings})
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ position: "absolute", left: 18, right: 18, bottom: 18 }}>
            <Chip
              label={currencyLabel}
              size="small"
              sx={{
                mb: 1.2,
                height: 28,
                bgcolor: "rgba(255,255,255,0.14)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.18)",
                backdropFilter: "blur(10px)",
                fontSize: "0.72rem",
                fontWeight: 700,
                fontFamily: '"exo", "Inter", sans-serif',
              }}
            />
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontSize: { xs: "1.05rem", sm: "1.18rem" },
                fontFamily: '"exo", "Inter", sans-serif',
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
                textShadow: "0 2px 10px rgba(0,0,0,0.32)",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {company.name}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: { xs: 2.1, sm: 2.6 },
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            minWidth: 0,
            position: "relative",
          }}
        >
          <Stack direction="row" spacing={1.2} alignItems="flex-start">
            <Avatar
              sx={{
                width: 44,
                height: 44,
                bgcolor: "rgba(249,115,91,0.10)",
                color: BRAND,
                border: "1px solid rgba(249,115,91,0.16)",
                fontSize: "1rem",
                fontWeight: 700,
                fontFamily: '"exo", "Inter", sans-serif',
              }}
            >
              {initial}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  color: INK,
                  fontSize: "1rem",
                  fontWeight: 700,
                  fontFamily: '"exo", "Inter", sans-serif',
                  lineHeight: 1.2,
                  letterSpacing: "-0.02em",
                  mb: 0.7,
                }}
              >
                {company.name}
              </Typography>
              {locationLabel && (
                <Stack direction="row" alignItems="center" spacing={0.8}>
                  <LocationOnOutlined sx={{ fontSize: 16, color: BRAND, flexShrink: 0 }} />
                  <Typography
                    color={SOFT_TEXT}
                    noWrap
                    sx={{
                      fontSize: "0.84rem",
                      fontWeight: 600,
                      fontFamily: '"Inter", sans-serif',
                    }}
                  >
                    {locationLabel}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>

          <Box
            sx={{
              p: 1.4,
              borderRadius: "16px",
              bgcolor: CLOUD,
              border: `1px solid ${alpha(BORDER, 0.9)}`,
            }}
          >
            <Typography
              sx={{
                color: MUTED,
                fontSize: "0.88rem",
                lineHeight: 1.72,
                fontFamily: '"Inter", sans-serif',
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {summaryText}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={company.isActive ? "Available for orders" : "Currently unavailable"}
              size="small"
              sx={{
                bgcolor: company.isActive ? "rgba(16,185,129,0.10)" : "rgba(148,163,184,0.14)",
                color: company.isActive ? "#047857" : "#475569",
                border: company.isActive
                  ? "1px solid rgba(16,185,129,0.16)"
                  : "1px solid rgba(148,163,184,0.18)",
                fontWeight: 700,
                fontSize: "0.72rem",
                fontFamily: '"exo", "Inter", sans-serif',
              }}
            />
            {rating > 0 && (
              <Chip
                icon={<StarRounded sx={{ fontSize: "0.95rem !important", color: "#F59E0B !important" }} />}
                label={`${rating.toFixed(1)} rating`}
                size="small"
                sx={{
                  bgcolor: "rgba(245,158,11,0.10)",
                  color: "#B45309",
                  border: "1px solid rgba(245,158,11,0.18)",
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  fontFamily: '"exo", "Inter", sans-serif',
                }}
              />
            )}
          </Stack>

          <Box sx={{ flex: 1 }} />

          <Divider sx={{ borderColor: BORDER, opacity: 0.9 }} />

          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.2}>
            <Box>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: SOFT_TEXT,
                  fontFamily: '"exo", "Inter", sans-serif',
                  mb: 0.35,
                }}
              >
                Explore Menu
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.84rem",
                  color: MUTED,
                  fontFamily: '"Inter", sans-serif',
                }}
              >
                View dishes and offers
              </Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              endIcon={<ArrowForward sx={{ fontSize: 15 }} />}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.86rem",
                fontFamily: '"exo", "Inter", sans-serif',
                minWidth: 0,
                px: 2.3,
                py: 1,
                bgcolor: BRAND,
                boxShadow: "0 10px 22px rgba(249,115,91,0.24)",
                "&:hover": {
                  bgcolor: BRAND_DARK,
                  boxShadow: "0 14px 28px rgba(249,115,91,0.30)",
                },
              }}
            >
              View Menu
            </Button>
          </Stack>
        </Box>
      </CardActionArea>
    </MotionCard>
  );
};

const AllRestaurants = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const initialSearch = queryParams.get("search") || "";
  const initialLocation = queryParams.get("location") || "";

  const [searchInput, setSearchInput] = useState(initialSearch);
  const [locationFilter, setLocationFilter] = useState(initialLocation);
  const [appliedSearch, setAppliedSearch] = useState(initialSearch);
  const [appliedLocation, setAppliedLocation] = useState(initialLocation);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [districts, setDistricts] = useState([]);

  const fetchCompanies = useCallback(async (searchVal, locationVal, pageNum) => {
    setLoading(true);
    try {
      const params = { page: pageNum - 1, limit: PAGE_SIZE };
      if (searchVal) params.search = searchVal;
      if (locationVal) params.district = locationVal;
      const res = await CompanyServices.getAll(params);
      if (res.success && res.data?.view) {
        const data = res.data.view.data || [];
        setCompanies(data);
        setTotalItems(res.data.view.totalItems || data.length);
        if (districts.length === 0) {
          const allDistricts = data.map((company) => company.district).filter(Boolean);
          setDistricts([...new Set(allDistricts)].sort());
        }
      } else {
        setCompanies([]);
        setTotalItems(0);
      }
    } catch (_) {
      setCompanies([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [districts.length]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    fetchCompanies(appliedSearch, appliedLocation, page);
  }, [appliedSearch, appliedLocation, page, fetchCompanies]);

  useEffect(() => {
    const loadDistricts = async () => {
      try {
        const res = await CompanyServices.getAll({ page: 0, limit: 100 });
        if (res.success && res.data?.view?.data) {
          const allDistricts = res.data.view.data.map((company) => company.district).filter(Boolean);
          setDistricts([...new Set(allDistricts)].sort());
        }
      } catch (_) {
        // no-op
      }
    };
    loadDistricts();
  }, []);

  const handleSearch = () => {
    setAppliedSearch(searchInput);
    setAppliedLocation(locationFilter);
    setPage(1);
    const params = new URLSearchParams();
    if (searchInput) params.set("search", searchInput);
    if (locationFilter) params.set("location", locationFilter);
    navigate(`/restaurants${params.toString() ? `?${params.toString()}` : ""}`, { replace: true });
  };

  const handleClear = () => {
    setSearchInput("");
    setLocationFilter("");
    setAppliedSearch("");
    setAppliedLocation("");
    setPage(1);
    navigate("/restaurants", { replace: true });
  };

  const handleRemoveSearchFilter = () => {
    setSearchInput("");
    setAppliedSearch("");
    setPage(1);
    const params = new URLSearchParams();
    if (appliedLocation) params.set("location", appliedLocation);
    navigate(`/restaurants${params.toString() ? `?${params.toString()}` : ""}`, { replace: true });
  };

  const handleRemoveLocationFilter = () => {
    setLocationFilter("");
    setAppliedLocation("");
    setPage(1);
    const params = new URLSearchParams();
    if (appliedSearch) params.set("search", appliedSearch);
    navigate(`/restaurants${params.toString() ? `?${params.toString()}` : ""}`, { replace: true });
  };

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const hasFilters = Boolean(appliedSearch || appliedLocation);

  return (
    <Box sx={{ minHeight: "100vh", background: "#fff" }}>
      <PublicHeader />

      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          pt: { xs: "calc(70px + 3.5rem)", md: "calc(80px + 4.75rem)" },
          pb: { xs: 5, md: 7 },
          background:
            "radial-gradient(circle at 8% 5%, rgba(14,165,233,0.14), transparent 35%), radial-gradient(circle at 90% 8%, rgba(249,115,91,0.20), transparent 40%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
          borderBottom: `1px solid ${BORDER}`,
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(15,23,42,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.04) 1px, transparent 1px)",
            backgroundSize: "42px 42px",
            opacity: 0.5,
            pointerEvents: "none",
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  border: `1px solid ${BORDER}`,
                  borderRadius: "999px",
                  px: 2,
                  py: 0.7,
                  bgcolor: "rgba(255,255,255,0.9)",
                  backdropFilter: "blur(4px)",
                }}
              >
                <Bolt sx={{ fontSize: 15, color: BRAND }} />
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: MUTED,
                    fontFamily: '"exo", "Inter", sans-serif',
                    letterSpacing: "0.02em",
                  }}
                >
                  Browse live menus from modern restaurants
                </Typography>
              </Box>
            </Box>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.06 }}>
            <Typography
              sx={{
                textAlign: "center",
                fontFamily: '"exo", "Inter", sans-serif',
                fontWeight: 700,
                fontSize: { xs: "2.4rem", sm: "3.5rem", md: "4.4rem" },
                lineHeight: 1.03,
                letterSpacing: "-0.03em",
                color: INK,
                mb: 1.5,
              }}
            >
              Find your next
              <Box
                component="span"
                sx={{
                  ml: 1.2,
                  background: "linear-gradient(135deg, #F9735B 0%, #FB923C 55%, #0EA5E9 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                favorite table
              </Box>
            </Typography>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.14 }}>
            <Typography
              sx={{
                textAlign: "center",
                fontSize: { xs: "0.98rem", md: "1.08rem" },
                color: MUTED,
                lineHeight: 1.8,
                fontFamily: '"Inter", sans-serif',
                maxWidth: 700,
                mx: "auto",
                mb: 4.5,
              }}
            >
              {totalItems > 0
                ? `Explore ${totalItems} restaurant${totalItems !== 1 ? "s" : ""} with live menus, clearer discovery, and a smoother dining experience.`
                : "Explore restaurants, scan live menus, and discover your next dining experience with ease."}
            </Typography>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.2 }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.2, sm: 1.35 },
                borderRadius: "18px",
                bgcolor: "rgba(255,255,255,0.95)",
                border: `1px solid ${BORDER}`,
                boxShadow: "0 18px 42px rgba(2,6,23,0.07)",
                maxWidth: 980,
                mx: "auto",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    px: 1.5,
                    borderRadius: "12px",
                    bgcolor: { xs: CLOUD, sm: "transparent" },
                  }}
                >
                  <Search sx={{ fontSize: 21, color: "#94A3B8", mr: 1 }} />
                  <InputBase
                    placeholder="Search restaurants, food styles, or dishes..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    sx={{
                      flex: 1,
                      fontSize: { xs: "0.9rem", sm: "0.96rem" },
                      fontFamily: '"Inter", sans-serif',
                      "& input": { py: 1.25 },
                    }}
                  />
                  {searchInput && (
                    <ClearOutlined
                      onClick={() => setSearchInput("")}
                      sx={{ fontSize: 18, color: "#94A3B8", cursor: "pointer" }}
                    />
                  )}
                </Box>

                <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" }, borderColor: BORDER }} />

                <FormControl size="small" sx={{ minWidth: 190, display: { xs: "none", sm: "flex" } }}>
                  <Select
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    displayEmpty
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: "0.92rem",
                      color: MUTED,
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                      "& .MuiSelect-select": { py: 1.35, pl: 1.5 },
                    }}
                    startAdornment={<LocationOnOutlined sx={{ fontSize: 18, color: "#94A3B8", mr: 0.75 }} />}
                  >
                    <MenuItem value="">All Locations</MenuItem>
                    {districts.map((district) => (
                      <MenuItem key={district} value={district} sx={{ fontFamily: '"Inter", sans-serif' }}>
                        {district}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" }, borderColor: BORDER }} />

                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                  {hasFilters && (
                    <Button
                      onClick={handleClear}
                      startIcon={<ClearOutlined />}
                      sx={{
                        display: { xs: "none", sm: "inline-flex" },
                        textTransform: "none",
                        fontWeight: 700,
                        color: MUTED,
                        fontFamily: '"exo", "Inter", sans-serif',
                        "&:hover": { bgcolor: CLOUD },
                      }}
                    >
                      Clear
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    startIcon={<TuneOutlined sx={{ display: { xs: "none", sm: "block" } }} />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      fontFamily: '"exo", "Inter", sans-serif',
                      px: { xs: 1.8, sm: 2.6 },
                      py: { xs: 1.05, sm: 1.2 },
                      borderRadius: "12px",
                      minWidth: { xs: 46, sm: "auto" },
                      bgcolor: BRAND,
                      boxShadow: "0 12px 24px rgba(249,115,91,0.24)",
                      "&:hover": { bgcolor: BRAND_DARK, boxShadow: "0 14px 28px rgba(249,115,91,0.3)" },
                    }}
                  >
                    <Box sx={{ display: { xs: "none", sm: "block" } }}>Apply Filters</Box>
                    <Search sx={{ display: { xs: "block", sm: "none" }, fontSize: 20 }} />
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ display: { xs: "flex", sm: "none" }, mt: 1, gap: 1, alignItems: "center" }}>
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    px: 1.5,
                    py: 0.2,
                    borderRadius: "12px",
                    bgcolor: CLOUD,
                  }}
                >
                  <LocationOnOutlined sx={{ fontSize: 18, color: "#94A3B8", mr: 0.75 }} />
                  <Select
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    displayEmpty
                    variant="standard"
                    disableUnderline
                    sx={{
                      flex: 1,
                      fontFamily: '"Inter", sans-serif',
                      fontSize: "0.88rem",
                      color: MUTED,
                      "& .MuiSelect-select": { py: 1, pr: "28px !important" },
                      "& .MuiSelect-icon": { right: 4 },
                    }}
                  >
                    <MenuItem value="">All Locations</MenuItem>
                    {districts.map((district) => (
                      <MenuItem key={district} value={district} sx={{ fontFamily: '"Inter", sans-serif' }}>
                        {district}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                {hasFilters && (
                  <Button
                    onClick={handleClear}
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: MUTED,
                      fontFamily: '"exo", "Inter", sans-serif',
                      px: 1.5,
                      borderRadius: "12px",
                      bgcolor: CLOUD,
                      "&:hover": { bgcolor: "#EEF2F7" },
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      <Box
        sx={{
          py: { xs: 5.5, md: 7.5 },
          bgcolor: "#fff",
          background:
            "radial-gradient(circle at 10% 10%, rgba(14,165,233,0.05), transparent 24%), radial-gradient(circle at 95% 6%, rgba(249,115,91,0.08), transparent 26%), linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
        }}
      >
        <Container maxWidth="lg">
          <AnimatePresence>
            {hasFilters && (
              <motion.div
                key="active-filters"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.28 }}
                style={{ overflow: "hidden" }}
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3.5 }}>
                  <Typography
                    sx={{
                      alignSelf: "center",
                      fontSize: "0.86rem",
                      color: SOFT_TEXT,
                      fontFamily: '"Inter", sans-serif',
                    }}
                  >
                    Active filters:
                  </Typography>
                  {appliedSearch && (
                    <Chip
                      label={`"${appliedSearch}"`}
                      onDelete={handleRemoveSearchFilter}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor: "rgba(249,115,91,0.1)",
                        color: BRAND,
                        border: "1px solid rgba(249,115,91,0.18)",
                        fontFamily: '"exo", "Inter", sans-serif',
                      }}
                    />
                  )}
                  {appliedLocation && (
                    <Chip
                      label={appliedLocation}
                      onDelete={handleRemoveLocationFilter}
                      size="small"
                      icon={<LocationOnOutlined sx={{ fontSize: "1rem !important" }} />}
                      sx={{
                        fontWeight: 700,
                        bgcolor: "rgba(14,165,233,0.10)",
                        color: "#0369A1",
                        border: "1px solid rgba(14,165,233,0.18)",
                        fontFamily: '"exo", "Inter", sans-serif',
                      }}
                    />
                  )}
                </Stack>
              </motion.div>
            )}
          </AnimatePresence>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
              mb: 4,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: 700,
                  color: INK,
                  fontFamily: '"exo", "Inter", sans-serif',
                  letterSpacing: "-0.02em",
                }}
              >
                All Restaurants
              </Typography>
              <Typography
                sx={{
                  mt: 0.6,
                  fontSize: "0.95rem",
                  color: MUTED,
                  fontFamily: '"Inter", sans-serif',
                }}
              >
                {loading
                  ? "Loading restaurant collection..."
                  : `${totalItems} result${totalItems === 1 ? "" : "s"} available`}
              </Typography>
            </Box>
            <Chip
              icon={<RestaurantMenuOutlined sx={{ fontSize: "1rem !important" }} />}
              label="Live restaurant discovery"
              sx={{
                bgcolor: "#FFF7F5",
                color: BRAND,
                border: "1px solid #FCDAD3",
                fontWeight: 700,
                fontFamily: '"exo", "Inter", sans-serif',
              }}
            />
          </Box>

          <Grid container spacing={{ xs: 2.2, md: 2.8 }}>
            {loading ? (
              Array.from({ length: PAGE_SIZE }).map((_, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index} sx={{ display: "flex" }}>
                  <RestaurantCardSkeleton />
                </Grid>
              ))
            ) : companies.length > 0 ? (
              companies.map((company) => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={company.id} sx={{ display: "flex" }}>
                  <RestaurantCard company={company} />
                </Grid>
              ))
            ) : (
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    textAlign: "center",
                    py: { xs: 8, md: 10 },
                    px: 3,
                    borderRadius: "24px",
                    border: `1px solid ${BORDER}`,
                    bgcolor: "rgba(255,255,255,0.92)",
                    boxShadow: "0 18px 40px rgba(15,23,42,0.06)",
                  }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "18px",
                      mx: "auto",
                      mb: 2.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(249,115,91,0.10)",
                      color: BRAND,
                    }}
                  >
                    <Search sx={{ fontSize: 32 }} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: "1.35rem", md: "1.6rem" },
                      fontWeight: 700,
                      color: INK,
                      fontFamily: '"exo", "Inter", sans-serif',
                      mb: 1,
                    }}
                  >
                    No restaurants found
                  </Typography>
                  <Typography
                    sx={{
                      color: MUTED,
                      mb: 3,
                      fontFamily: '"Inter", sans-serif',
                      maxWidth: 520,
                      mx: "auto",
                      lineHeight: 1.75,
                    }}
                  >
                    Try a broader search, switch location filters, or clear everything to explore the full restaurant list.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleClear}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      fontFamily: '"exo", "Inter", sans-serif',
                      px: 3.2,
                      py: 1.3,
                      borderRadius: "12px",
                      bgcolor: BRAND,
                      "&:hover": { bgcolor: BRAND_DARK },
                    }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>

          <AnimatePresence>
            {!loading && totalPages > 1 && (
              <motion.div
                key="pagination"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.35 }}
              >
                <Box display="flex" justifyContent="center" mt={{ xs: 5, md: 6 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => {
                      setPage(value);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    color="primary"
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      "& .MuiPaginationItem-root": {
                        fontFamily: '"exo", "Inter", sans-serif',
                        fontWeight: 700,
                        color: MUTED,
                        borderRadius: "10px",
                      },
                      "& .Mui-selected": {
                        bgcolor: `${BRAND} !important`,
                        color: "#fff",
                      },
                    }}
                  />
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </Container>
      </Box>
    </Box>
  );
};

export default AllRestaurants;
